import React, { useState, useContext, useEffect } from 'react';
import { navigate } from 'gatsby';

import { DealerSearchContext } from '@rsc/contexts/DealerSearchContext';
import { LocaleContext } from '@rsc/contexts/LocaleContext';

import { Button } from '@rsc/ui-components/shadcn/Button';
import { Skeleton } from '@rsc/ui-components/shadcn/Skeleton';
import { LocaleCurrencyEnum } from '@rsc/models/LocaleCurrencyEnum';

import SearchForm from './SearchForm';
import SearchResults from './SearchResults';
import SelectedLocation from './SelectedLocation';

import {
  FormErrors,
  DealerContextProps,
  BlockBuilderDynamicDealerPricingProps,
} from './BlockBuilderDynamicDealerPricing.d';

function BlockBuilderDynamicDealerPricing({
  value,
  getInternalLink,
}: Readonly<BlockBuilderDynamicDealerPricingProps>) {
  const dealerSearchContext =
    useContext<DealerContextProps>(DealerSearchContext);
  const localeContext = useContext(LocaleContext);
  const storedDealer = dealerSearchContext?.storedDealer;
  const currency = localeContext?.currency || LocaleCurrencyEnum.USD;

  const [activeDealer, setActiveDealer] = useState(null);
  const [isCompanyOwnedDealer, setIsCompanyOwnedDealer] = useState(false);
  const [zipCode, setZipCode] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({ zipCode: '' });
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleChangeLocation = () => {
    setActiveDealer(null);
    setErrors({});
    dealerSearchContext?.setStoredDealer(null);
  };

  const handleCTA = () => {
    const cta = value?.cta;

    if (cta?.linkType === 'internal') {
      navigate(
        getInternalLink({
          ...cta,
          reference: cta?.internalLink,
          currency,
        })
      );
    }

    if (cta?.linkType === 'external') {
      window.open(cta?.href, cta?.blank ? '_blank' : '_self').focus();
    }
  };

  useEffect(() => {
    if (storedDealer?.name || storedDealer?.address?.zip) {
      setActiveDealer(storedDealer);
      setIsCompanyOwnedDealer(storedDealer?.isCOS || false);
      setInitLoading(false);
    } else {
      setInitLoading(false);
    }
  }, [storedDealer]);

  return (
    <div className="dynamic-dealer-pricing flex flex-col items-center gap-6 my-8">
      {value?.headline && <h2 className="m-0 text-center">{value.headline}</h2>}
      <div className="bg-muted border border-border rounded-md flex flex-col gap-7 p-4 min-h-64 max-w-3xl items-center self-center w-full">
        {/* Loading State */}
        {!activeDealer && initLoading && (
          <div className="flex flex-col gap-1">
            <Skeleton className="h-[14px] w-full" />
            <Skeleton className="h-[128px] w-full" />
          </div>
        )}

        {/* Search Form */}
        {!activeDealer && !initLoading && (
          <SearchForm
            setLoading={setLoading}
            loading={loading}
            setErrors={setErrors}
            errors={errors}
            setZipCode={setZipCode}
            zipCode={zipCode}
            setIsCompanyOwnedDealer={setIsCompanyOwnedDealer}
            setActiveDealer={setActiveDealer}
            dealerSearchLabel={value?.dealerSearchLabel}
            dealerSearchDescription={value?.dealerSearchDescription}
            dealerUnavailableMessage={value?.dealerUnavailableMessage}
            getInternalLink={getInternalLink}
          />
        )}

        {!loading && activeDealer && !initLoading && (
          <div className="flex flex-col gap-7 items-center w-full max-w-screen-sm">
            {/* Company Owned Dealer Results */}
            {isCompanyOwnedDealer && (
              <>
                <SelectedLocation
                  zipCode={zipCode}
                  activeDealer={activeDealer}
                  locationSearchHeading={value.codLocationSearchHeading}
                  handleChangeLocation={handleChangeLocation}
                />

                <SearchResults
                  isCompanyOwnedDealer={isCompanyOwnedDealer}
                  pricingColumnHeader={value.codPricingColumnHeader}
                  leftColumnShowPrice={value.codLeftColumnShowPrice}
                  leftColumnUseCustomPrice={value.codLeftColumnUseCustomPrice}
                  leftColumnCustomPrice={value.codLeftColumnCustomPrice}
                  leftColumnVariant={value.codLeftColumnVariant}
                  leftColumnPriceDescriptorText={
                    value.codLeftColumnPriceDescriptorText
                  }
                  leftColumnPaymentDetailText={
                    value.codLeftColumnPaymentDetailText
                  }
                  rightColumnShowPrice={value.codRightColumnShowPrice}
                  rightColumnUseCustomPrice={value.codRightColumnUseCustomPrice}
                  rightColumnCustomPrice={value.codRightColumnCustomPrice}
                  rightColumnVariant={value.codRightColumnVariant}
                  rightColumnPriceDescriptorText={
                    value.codRightColumnPriceDescriptorText
                  }
                  rightColumnPaymentDetailText={
                    value.codRightColumnPaymentDetailText
                  }
                  getInternalLink={getInternalLink}
                  product={value.product}
                />
              </>
            )}
            {/* Independent Dealer Results */}
            {!isCompanyOwnedDealer && (
              <>
                <SelectedLocation
                  zipCode={zipCode}
                  activeDealer={activeDealer}
                  locationSearchHeading={value.idLocationSearchHeading}
                  handleChangeLocation={handleChangeLocation}
                  className="w-full"
                />

                <SearchResults
                  isCompanyOwnedDealer={isCompanyOwnedDealer}
                  pricingHeader={value.idPricingHeader}
                  paymentDetailText={value.idPricingDetailText}
                  getInternalLink={getInternalLink}
                />
              </>
            )}
          </div>
        )}

        {value?.cta && (
          <div className="text-center">
            <Button onClick={handleCTA}>{value.cta?.caption || ''}</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BlockBuilderDynamicDealerPricing;
